import { AppwriteException, ID, OAuthProvider } from 'appwrite';
import { appwriteConfig } from '../../config/environment';
import { permissionsForRoles } from '../../config/permissions';
import type { SessionProfile } from '../../types/session';
import { account, tablesDB, teams } from './client';

const USER_CACHE_KEY = 'ta-auth-user';
const TEAMS_CACHE_KEY = 'ta-auth-teams';

export interface SessionGateway {
  load(): Promise<SessionProfile | null>;
  signInWithGoogle(): Promise<void>;
  /** Cuenta ya existente. */
  signInWithPassword(email: string, password: string): Promise<void>;
  /** Crea la cuenta y abre sesión de una — el perfil ampliado se completa
   * después, en el registro que ya bloquea el resto del sitio. */
  registerWithPassword(email: string, password: string, name: string): Promise<void>;
  /** Manda el correo con el enlace para elegir una contraseña nueva. */
  requestPasswordReset(email: string): Promise<void>;
  /** Confirma la contraseña nueva, con los datos que trae el enlace del correo. */
  confirmPasswordReset(userId: string, secret: string, password: string): Promise<void>;
  signOut(): Promise<void>;
}

function unauthorized(error: unknown): boolean {
  if (error instanceof AppwriteException) return error.code === 401 || error.code === 403;
  if (typeof error !== 'object' || error === null || !('code' in error)) return false;
  const code = Number((error as { code?: unknown }).code);
  return code === 401 || code === 403;
}

function notFound(error: unknown): boolean {
  if (error instanceof AppwriteException) return error.code === 404;
  if (typeof error !== 'object' || error === null || !('code' in error)) return false;
  return Number((error as { code?: unknown }).code) === 404;
}

function networkFailure(error: unknown): boolean {
  if (error instanceof TypeError) return true;
  return error instanceof Error && /failed to fetch|network|load failed|fetch failed/i.test(error.message);
}

async function withNetworkRetry<T>(operation: () => Promise<T>): Promise<T> {
  try {
    return await operation();
  } catch (error) {
    if (!networkFailure(error)) throw error;
    await new Promise((resolve) => window.setTimeout(resolve, 350));
    return operation();
  }
}

function clearCache(): void {
  try {
    localStorage.removeItem(USER_CACHE_KEY);
    localStorage.removeItem(TEAMS_CACHE_KEY);
  } catch {
    // Appwrite conserva la autoridad aunque el navegador bloquee el almacenamiento.
  }
}

function saveCache(profile: SessionProfile): void {
  try {
    localStorage.setItem(USER_CACHE_KEY, JSON.stringify({
      id: profile.user.id,
      name: profile.user.name,
      email: profile.user.email,
      at: Date.now(),
    }));
    localStorage.setItem(TEAMS_CACHE_KEY, JSON.stringify({ ids: profile.teams, at: Date.now() }));
  } catch {
    // La sesión real continúa aunque no pueda guardarse una copia visual.
  }
}

function stringValue(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

async function load(): Promise<SessionProfile | null> {
  let currentUser;
  try {
    currentUser = await withNetworkRetry(() => account.get());
  } catch (error) {
    if (unauthorized(error)) {
      clearCache();
      return null;
    }
    throw error;
  }

  let preferredName = '';
  let countryCode = '';
  let timezone = '';
  let profileExists = false;
  try {
    const row = await withNetworkRetry(() => tablesDB.getRow({
      databaseId: appwriteConfig.databaseId,
      tableId: appwriteConfig.profilesTableId,
      rowId: currentUser.$id,
    }));
    const values = row as unknown as Record<string, unknown>;
    preferredName = stringValue(values.preferredName);
    countryCode = stringValue(values.countryCode);
    timezone = stringValue(values.timezone);
    profileExists = true;
  } catch (error) {
    if (!notFound(error)) throw error;
  }

  let teamIds: string[] = [];
  try {
    const result = await withNetworkRetry(() => teams.list());
    teamIds = result.teams.map((team) => team.$id);
  } catch (error) {
    if (unauthorized(error)) throw error;
    console.error('[Club Accesible] No se pudieron consultar los equipos.', error);
  }

  // "admin" no es un equipo aparte: es un rol dentro de la membresía de
  // "staff" (igual que en el backend, ver auth/staff.py::require_admin). Solo
  // se consulta si la cuenta ya es del equipo; quien no lo es no puede listar
  // esa membresía de todos modos.
  let roles: string[] = [];
  if (teamIds.includes('staff')) {
    try {
      const memberships = await withNetworkRetry(() => teams.listMemberships({ teamId: 'staff' }));
      const own = memberships.memberships.find((membership) => membership.userId === currentUser.$id);
      // Compatibilidad con membresías anteriores al catálogo de permisos.
      roles = own ? (own.roles.length ? [...own.roles] : ['staff']) : [];
    } catch (error) {
      if (unauthorized(error)) throw error;
      console.error('[Club Accesible] No se pudo consultar el rol dentro del equipo.', error);
    }
  }

  const profile: SessionProfile = {
    user: {
      id: currentUser.$id,
      name: currentUser.name || '',
      email: currentUser.email || '',
    },
    profile: {
      exists: profileExists,
      preferredName,
      countryCode,
      timezone,
    },
    teams: teamIds,
    roles,
    permissions: permissionsForRoles(roles),
    // Sistemas es un eje independiente: `sysadmin` no hereda administración.
    isStaff: roles.includes('staff') || roles.includes('admin'),
    isAdmin: roles.includes('admin'),
    isSysadmin: roles.includes('sysadmin'),
  };
  saveCache(profile);
  return profile;
}

async function signInWithGoogle(): Promise<void> {
  const callback = new URL(window.location.href);
  callback.searchParams.delete('auth');
  await account.createOAuth2Session({
    provider: OAuthProvider.Google,
    success: callback.toString(),
    failure: `${callback.toString()}${callback.search ? '&' : '?'}auth=failed`,
  });
}

async function signInWithPassword(email: string, password: string): Promise<void> {
  await account.createEmailPasswordSession({ email: email.trim(), password });
}

async function registerWithPassword(email: string, password: string, name: string): Promise<void> {
  await account.create({ userId: ID.unique(), email: email.trim(), password, name: name.trim() });
  await account.createEmailPasswordSession({ email: email.trim(), password });
}

async function requestPasswordReset(email: string): Promise<void> {
  const url = new URL('/panel/recuperar-contrasena', window.location.origin);
  await account.createRecovery({ email: email.trim(), url: url.toString() });
}

async function confirmPasswordReset(userId: string, secret: string, password: string): Promise<void> {
  await account.updateRecovery({ userId, secret, password });
}

async function signOut(): Promise<void> {
  try {
    await account.deleteSession({ sessionId: 'current' });
  } finally {
    clearCache();
  }
}

export const appwriteSessionGateway: SessionGateway = {
  load,
  signInWithGoogle,
  signInWithPassword,
  registerWithPassword,
  requestPasswordReset,
  confirmPasswordReset,
  signOut,
};
